import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { parseISO } from 'date-fns';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Breadcrumb from '../../Components/Breadcrumb';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, PaperClipIcon } from '@heroicons/react/20/solid'
import HeaderWithBreadcrumb from '../../Components/Headers/HeaderWithBreadcrumb';
import Stats from '../../Components/Stats';
import Tabs, { useTabs } from '../../Components/Tabs';
import { EyeIcon, UserIcon, TagIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import Feeds from '../../Components/Feeds';
import Badge, { badgeColorsChoices } from '../../Components/Badge';
import { useRefresh } from '../../Hooks/useRefresh';
import Modal from '../../Components/Modal';
import JSONViewer from '../../Components/JSONViewer/JSONViewer';
import { Button } from '../../Components/Buttons';
import Select from '../../Components/Inputs/Select';
import { TextInput } from '../../Components/Inputs/TextInput';
import { getTagEventName } from '../Automatisations/Automatisations';
import { ServerStackIcon } from '@heroicons/react/24/solid';
import { decompressData } from '../../Utils';

export default function Contact() {

    const { translate, language, setLanguage } = useTranslation();
    const { formatDate, getDaysSince } = useDateFormat();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();
    const appState = useAppState();
    const user = appState.user;

    const [contact, setContact] = useState({});
    const [events, setEvents] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const pagination = usePaginations();
    const { refreshId, refresh } = useRefresh();
    const [open, setOpen] = useState(false);
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const { page, pageSize } = pagination;

    const contactId = encodeURIComponent(useParams().contactId);

    const [customFields, setCustomFields] = useState([]);

    const [customFieldsConfiguration, setCustomFieldsConfiguration] = useState([]);


    useEffect(() => {
        const fetchContact = async () => {
            try {
                setLoading(true);
                const response = await api.getContact(contactId, page, pageSize);
                setContact(response.data.contact);
                pagination.setMetaData(response.data.meta);
                setEvents(response.data.events);
                setTags(response.data.tags);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchContact();
    }, [pagination.page, refreshId]);

    useEffect(() => {
        const fetchCustomFields = async () => {
            const response = await api.listCustomFields(1, 9999);
            setCustomFieldsConfiguration(response.data.customFields);
        }
        fetchCustomFields();
    }, []);


    const pages = useMemo(() => [
        { name: 'Contacts', href: '/contacts', current: false },
        { name: contact?.external_id || '', href: '#', current: true },
    ], [contact]);


    useEffect(() => {
        if (!contact || !contact.data) return;
        setCustomFields(decompressData(contact.data));
    }, [contact]);

    const setCustomFieldOpen = useCallback((field, open) => {
        field.customFieldOpen = open;
        setCustomFields([...customFields]);
    }, [customFields]);

   
    const tabsData = useMemo(() => [
        { id: 'information', name: translate('contact.information'), href: '#', icon: UserIcon, current: false },
        { id: 'data_warehouse', name: translate('contact.data_warehouse'), href: '#', icon: ServerStackIcon, current: false },
        { id: 'events', name: translate('contact.events'), href: '#', icon: EyeIcon, current: false },
        { id: 'tags', name: translate('contact.tags'), href: '#', icon: TagIcon, current: false },
    ], [contact]);

    const tabs = useTabs(tabsData);
    const { currentTab, currentTabIndex, setCurrentTab } = tabs;

    const stats = [
        { name: translate('contact.number_of_events'), value: pagination.metaData.total_records, change: '', changeType: 'positive' },
        { name: translate('contact.last_event_date'), 
          value: events.length > 0 ? formatDate(events[0].created_at) : '', 
          change: events.length > 0 ? `${translate('contact.since')} ${getDaysSince(events[0].created_at)} ${translate('days')}` : '', 
          changeType: 'positive' },
        { name: translate('contact.last_event_name'), value: events.length > 0 ? getTagEventName(events[0].name, tags, translate) : '', change: '', changeType: 'positive' },
        { name: translate('contact.created_at'), 
          value: contact && contact.created_at ? formatDate(contact.created_at) : '', 
          change: contact && contact.created_at ? `${translate('contact.since')} ${getDaysSince(contact.created_at)} ${translate('days')}` : '', 
          changeType: 'positive' },
    ]

    return (
        <>
        <Modal open={open} 
            onClose={() => setOpen(false)} 
            maxWidth="sm:max-w-2xl"
            title={translate('contact.event_properties')} 
            description={translate('contact.event_properties_description')} 
            children={<div className="max-h-[70vh] overflow-y-auto">
                <JSONViewer data={selectedEvent ? JSON.parse(selectedEvent.data) : null} />
            </div>} 
        />
        <AddTagsModal contactId={contact?.id} refresh={refresh} open={tagModalOpen} setOpen={setTagModalOpen} />
        <div className="flex min-h-full w-full">
            <SidebarNav currentPage="contacts">
                {loading ? <div className="flex justify-center items-center h-full w-12">
                    <LoadingCircleTransparent />
                </div> : (
                    <div>
                        <HeaderWithBreadcrumb pages={pages} title={contact?.external_id} actions={[]} />
                        <div className="flex flex-row">
                            <div className="mt-8 flex flex-col border border-gray-200">
                                <Stats stats={stats} />
                            </div>
                        </div>
                        <div className="mt-8 flex flex-col">
                            <Tabs tabs={tabs} />
                        </div>
                        {currentTabIndex === 0 && <InformationTab customFieldsConfiguration={customFieldsConfiguration} contact={contact} customFields={customFields} setCustomFieldOpen={setCustomFieldOpen} />}
                        {currentTabIndex === 1 && <DataWarehouseTab customFieldsConfiguration={customFieldsConfiguration} contact={contact} customFields={customFields} setCustomFieldOpen={setCustomFieldOpen} refresh={refresh} clientKey={user?.client_key} />}
                        {currentTabIndex === 2 && <EventsTab events={events} pagination={pagination} setOpen={setOpen} setSelectedEvent={setSelectedEvent} tags={tags} />}
                        {currentTabIndex === 3 && <TagsTab setTagModalOpen={setTagModalOpen} refresh={refresh} tags={tags} pagination={pagination} contactId={contact?.id} />}
                    </div>
                )}
            </SidebarNav>
        </div>
        </>
    );
}

const EventsTab = ({events, pagination, setOpen, setSelectedEvent, tags}) => {

    const { formatDate } = useDateFormat();
    const api = useAPI();

    const [automatisations, setAutomatisations] = useState([]);

    useEffect(() => {
        const fetchAutomatisations = async () => {
            try {
                const response = await api.listAllAutomatisations();
                setAutomatisations(response.data.automatisations);
            } catch (error) {
                console.error(error);
            }
        }

        fetchAutomatisations();
    }, []);

    const { translate } = useTranslation();

    const dataMapped = events.map((event) => {

        const automatisation = automatisations.find(automatisation => automatisation.entry_event_name === event.name);
        return {
            name: event.name,
            content: '',
            icon: CheckBadgeIcon,
            iconBackground: 'bg-green-500',
            datetime: formatDate(event.created_at),
            date: formatDate(event.created_at, 'MMM dd HH:mm, yyyy'),
            href: '#',
            target: getTagEventName(event.name, tags, translate),
            created_at: formatDate(event.created_at),
            data: JSON.stringify(event.data),
            link: automatisation ? {
                name: translate('contact.automatisation'),
                href: `/automatisation/${automatisation.id}`
            } : null
        }
    });

    return (
        <div className="flex flex-col pt-8 max-w-2xl">
            <Feeds events={dataMapped} translationKey="contact.events_table" columns={['name', 'created_at', 'data']} pagination={pagination} onClick={(row) => {setSelectedEvent(row); setOpen(true)}}/>
        </div>
    )
}

export const AddTagsModal = ({open, setOpen, contactId, refresh, allContact = false, filters = []}) => {
    const [tags, setTags] = useState([]);
    const api = useAPI();
    const [selectedTag, setSelectedTag] = useState(null);
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const fetchTags = async () => {
            const response = await api.listTags(1, 9999);
            setTags(response.data.tags);
        }
        fetchTags();
    }, []);

    const { translate } = useTranslation();

    const addTag = async () => {
        try {
            setLoading(true);
            var response = null;
            if (allContact) {
                response = await api.bulkAddAllContactsToTag(selectedTag.id, filters);
            } else {
                response = await api.bulkAddContactToTag(selectedTag.id, [contactId]);
            }

            dispatch({ 
                type: ActionTypes.SET_NOTIFICATION, 
                payload: { 
                    show: true, 
                    message: translate('contacts.add_tags_success').replace('%1$d', response.data.contact_tag_count), 
                    type: 'success', 
                    title: translate('contacts.success') 
                } 
            });
        } catch (error) {
            console.error(error);
            dispatch({ 
                type: ActionTypes.SET_NOTIFICATION, 
                payload: { 
                    show: true, 
                    message: translate('contacts.add_tags_timeout'),
                    type: 'warning',
                    title: translate('contacts.warning')
                } 
            });
        } finally {
            setLoading(false);
            setOpen(false);
            refresh();
        }
    }

    return (
        <Modal 
        open={open} 
        onClose={() => setOpen(false)} 
        title={translate('contact.add_tag')} 
        description={translate('contact.add_tags_description')} 
        children={<div className="flex flex-col gap-4">
            <div>
                {loading && <div className="flex justify-center items-center w-12"><LoadingCircleTransparent /></div>}
                <Select selected={selectedTag} setSelected={(value) => setSelectedTag(value)} options={tags.map((tag) => ({...tag, label: tag.name, value: tag.id}))} />
            </div>
            
            <div className="flex flex-row justify-end">
                <Button className="text-white" onClick={addTag} disabled={loading}>{translate('contact.add_tag')}</Button>
            </div>
        </div>} />
    )
}

const TagsTab = ({refresh, tags, pagination, contactId, setTagModalOpen}) => {

    const api = useAPI();
    const removeTag = async (tagId) => {
        try {
            await api.bulkRemoveContactsFromTag(tagId, [contactId]);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const { translate } = useTranslation();


    return (
        <div className="flex flex-col pt-8 max-w-7xl">
             <div className="flex flex-row items-center justify-between flex-wrap mb-4 w-full">
                <div className="flex flex-col items-start">
                    <p className="text-lg font-bold">{translate('contact.tags')}</p>
                    <p className="text-sm text-gray-500">{translate('contact.tags_description')}</p>
                </div>
                <div className="flex flex-col">
                    <Button className="text-white" onClick={() => {setTagModalOpen(true)}}>{translate('contact.add_tag')}</Button>
                </div>
            </div>
            <div className="flex flex-row gap-4 flex-wrap">
                {tags.map((tag, index) => (
                    <div key={index}>
                        <Badge onClickRemove={() => {
                            removeTag(tag.id);
                        }} text={tag.name} color={tag.color} />
                    </div>
                  
                ))}
            </div>
        </div>
    )
}

export const CustomField = ({ field, setCustomFieldOpen }) => {
    return (
        field.type === 'object' ? (
            <div key={field.name} className="py-6 flex flex-col justify-center">
                <div className="flex flex-row items-center cursor-pointer hover:opacity-75" onClick={() => setCustomFieldOpen(field, !field.customFieldOpen)}>
                    <dt className="text-lg font-bold text-gray-900 flex-1">{field.label || field.name}</dt>
                    {field.customFieldOpen ? <ChevronUpIcon className="w-6 h-6 text-gray-400" /> :
                        <ChevronDownIcon className="w-6 h-6 text-gray-400" />}
                </div>
                {field.customField && field.customFieldOpen && <div key={field.name + 'customFieldOpen'} className="flex flex-col pl-4 divide-y divide-gray-100">
                    {field.customField.map((subField) => (<div key={subField.name + subField.value}><CustomField field={subField} setCustomFieldOpen={setCustomFieldOpen} /></div>))}
                </div>}
            </div>
        ) : (
            <div key={field.name + field.value} className="py-6 flex flex-col">
                <dt className="text-lg font-bold text-gray-900">{field.label || field.name}</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{field.value}</dd>
            </div>
        )
    )
}


const InformationTab = ({contact, customFields, setCustomFieldOpen, customFieldsConfiguration}) => {
    const { translate } = useTranslation();
    const { formatDate } = useDateFormat();

    const customFieldsMappedToConfiguration = customFieldsConfiguration ? customFields.filter(field => customFieldsConfiguration.find(configuration => configuration.variable_name === field.name)).map(field => ({...field, label: customFieldsConfiguration.find(configuration => configuration.variable_name === field.name).label})) : [];

    const customFieldsMappedOtherThanConfiguration = customFieldsConfiguration ? customFields.filter(field => !customFieldsConfiguration.find(configuration => configuration.variable_name === field.name)) : [];
    
    return (
        <div className="flex flex-col mt-8 border-r border-gray-200 pr-8">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-base/7 font-semibold text-gray-900">{translate('contact.information')}</h3>
                                <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">{translate('contact.information_description')}</p>
                            </div>
                            <div className="mt-6 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                        <dt className="text-sm/6 font-medium text-gray-900">{translate('contact.id')}</dt>
                                        <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{contact.external_id}</dd>
                                    </div>
                                    {customFieldsMappedToConfiguration.map((field) => (
                                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                            <dt className="text-sm/6 font-medium text-gray-900">{field.label}</dt>
                                            <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{field.value}</dd>
                                        </div>
                                    ))}
                                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                        <dt className="text-sm/6 font-medium text-gray-900">{translate('contact.created_at')}</dt>
                                        <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {formatDate(contact.created_at)}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
    )
}

const DataWarehouseTab = ({contact, customFields, setCustomFieldOpen, customFieldsConfiguration, refresh, clientKey}) => {
    const { translate } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <div className="flex flex-col mt-8 border-r border-gray-200 pr-8">
                            <div className="px-4 sm:px-0">
                                <div className="flex flex-row items-center justify-between flex-wrap">
                                    <div>
                                        <h3 className="text-base/7 font-semibold text-gray-900">{translate('contact.data_warehouse')}</h3>
                                        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">{translate('contact.data_warehouse_description')}</p>
                                    </div>
                                    <Button className="text-white" onClick={() => setOpen(true)} disabled={!contact?.external_id}>{translate('contact.add_variable')}</Button>
                                </div>
                            </div>
                            <div className="mt-6 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100 ml-4">
                                    {customFields.map((field) => (<div key={field.name + field.value}><CustomField setCustomFieldOpen={setCustomFieldOpen} field={field} /></div>))}
                                </dl>
                            </div>
                            <Modal
                                open={open}
                                onClose={() => setOpen(false)}
                                title={translate('contact.add_variable')}
                                description={translate('contact.add_variable_description')}
                                children={<IdentifyModal refresh={refresh} setOpen={setOpen} open={open} contact={contact} clientKey={clientKey} />}
                            />
                        </div>
    )
}

const IdentifyModal = ({ refresh, setOpen, open, contact, clientKey }) => {

    const [variableName, setVariableName] = useState('');
    const [variableValue, setVariableValue] = useState('');
    const [loading, setLoading] = useState(false);
    const { translate } = useTranslation();
    const api = useAPI();

    const handleSave = async () => {
        try {
            setLoading(true);

            const parsedValue = Number(variableValue);
            const finalValue = isNaN(parsedValue) ? variableValue : parsedValue;

            await api.identifyContact(contact.external_id, {
                [variableName]: finalValue
            }, clientKey);

            refresh();

            setOpen(false);
            setVariableName('');
            setVariableValue('');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!open) {
            setVariableName('');
            setVariableValue('');
        }
    }, [open]);

    const buttonDisabled = loading || !variableName || variableName.length === 0 || !variableValue || variableValue.length === 0;

    return (
        <div className="mt-4 overflow-visible">
            <TextInput id="identify_variable_name" name="identify_variable_name" label={translate('contact.variable_name')} placeholder={translate('contact.variable_name_placeholder')} value={variableName} onChange={(e) => setVariableName(e.target.value)} />
            <div className="flex flex-col mt-4">
                <TextInput id="identify_variable_value" name="identify_variable_value" label={translate('contact.variable_value')} placeholder={translate('contact.variable_value_placeholder')} value={variableValue} onChange={(e) => setVariableValue(e.target.value)} />
            </div>

            <div className="flex flex-row justify-end mt-4">
                {loading && <div className="flex items-center mr-4 w-12"><LoadingCircleTransparent /></div>}
                <Button disabled={buttonDisabled} onClick={handleSave}>{translate('save')}</Button>
            </div>
        </div>
    )
}